import React, { useState, useEffect } from 'react';
import { Alert, Typography, Box } from '@mui/material';
import { Stack } from '@mui/system';
import { useNavigate, useParams } from 'react-router';
import { handleError, handleSuccess, handleInfo } from '../../../helpers/utils';
import { SummaryModal } from '../addProspect/SummaryModal';
import { TrafficLightFormStep } from '../addProspect/TrafficLightFormStep';
import { SalesValueStep } from '../addProspect/SalesValueStep';
import { ObservationsStep } from '../addProspect/ObservationsStep';
import { ProspectsStepper } from '../addProspect/ProspectsStepper';
import { ProspectsActions } from '../addProspect/ProspectsActions';
import { validateInput } from '../../../helpers/validateInput';
import { getCallById, updateCall } from '../../../actions/prospections';
import { format2Date } from '../../../helpers/hours';
import { ClientInformationEdit } from '../addProspect/ClientInformationEdit';

const steps = [
	'Información del Cliente',
	'Contacto',
	'Valor de Venta y Motivo de No Compra',
	'Observaciones',
];

const initialForm = {
	reference: '',
	clientType: '',
	clientName: '',
	email: '',
	phone: '',
	action: '',
	trafficLightContact: '',
	date1: '',
	trafficLightAppointment: '',
	date2: '',
	trafficLightLead: '',
	date3: '',
	trafficLightQuote: '',
	date4: '',
	trafficLightClosing: '',
	date5: '',
	saleValue: '',
	reasonNoPurchase: '',
	observations: '',
	adviser: '',
};

export const EditProspecting = () => {
	const { id } = useParams();
	const [isLoading, setIsLoading] = useState(false);
	const [activeStep, setActiveStep] = useState(0);
	const [form, setForm] = useState(initialForm);
	const [formTouched, setFormTouched] = useState({});
	const [showSummary, setShowSummary] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		if (id) {
			setIsLoading(true);
			getCallById(id)
				.then((response) => {
					const data = response.data;
					setForm({
						reference: data.reference || '',
						clientType: data.crm_type || '',
						clientName: data.name || '',
						email: data.email || '',
						phone: data.phone_number || '',
						action: data.action || '',
						trafficLightContact: data.contact || '',
						date1: data.first_date || '',
						trafficLightAppointment: data.appointment || '',
						date2: data.second_date || '',
						trafficLightLead: data.lead || '',
						date3: data.third_date || '',
						trafficLightQuote: data.quotation || '',
						date4: data.fourth_date || '',
						trafficLightClosing: data.closing || '',
						date5: data.fifth_date || '',
						saleValue: data.sale_value?.toString() || '',
						reasonNoPurchase: data.non_purchase || '',
						observations: data.observations || '',
						adviser: data.adviser || '',
					});
				})
				.catch(() => {
					handleError('Error al cargar los datos de la prospección');
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, [id]);

	const handleUpdateForm = (field, value) => {
		setFormTouched({ ...formTouched, [field]: true });
		setForm({ ...form, [field]: value });
	};

	const handleNext = () => {
		setActiveStep((prevStep) => prevStep + 1);
	};

	const handleBack = () => {
		setActiveStep((prevStep) => prevStep - 1);
	};

	const handleCancel = () => {
		setForm(initialForm);
		setActiveStep(0);
		navigate('/prospecciones');
	};

	const handleShowSummary = () => {
		handleInfo('Verifique la información antes de proceder.');
		setShowSummary(true);
	};

	const handleCloseSummary = () => {
		setShowSummary(false);
	};

	const handleSubmit = () => {
		setIsLoading(true);
		const updatedCallData = {
			reference: form.reference || null,
			crm_type: form.clientType || null,
			name: form.clientName || null,
			email: form.email || null,
			phone_number: form.phone || null,
			action: form.action || null,
			contact: form.trafficLightContact || null,
			appointment: form.trafficLightAppointment || null,
			lead: form.trafficLightLead || null,
			quotation: form.trafficLightQuote || null,
			closing: form.trafficLightClosing || null,
			sale_value:
				parseFloat(form.saleValue.replace(/[^\d.-]/g, '')) || null,
			non_purchase: form.reasonNoPurchase || null,
			observations: form.observations || null,
			adviser: form.adviser || null,
			first_date: format2Date(form.date1, 'YYYY-MM-DD'),
			second_date: format2Date(form.date2, 'YYYY-MM-DD'),
			third_date: format2Date(form.date3, 'YYYY-MM-DD'),
			fourth_date: format2Date(form.date4, 'YYYY-MM-DD'),
			fifth_date: format2Date(form.date5, 'YYYY-MM-DD'),
		};

		updateCall(id, updatedCallData)
			.then(() => {
				handleSuccess('Prospección actualizada correctamente.');
				navigate('/prospecciones');
			})
			.catch(() => {
				handleError('Error al actualizar la prospección');
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const renderStepContent = (step) => {
		switch (step) {
			case 0:
				return (
					<ClientInformationEdit
						form={form}
						handleUpdateForm={handleUpdateForm}
					/>
				);
			case 1:
				return (
					<TrafficLightFormStep
						form={form}
						handleUpdateForm={handleUpdateForm}
					/>
				);
			case 2:
				return (
					<SalesValueStep
						form={form}
						handleUpdateForm={handleUpdateForm}
					/>
				);
			case 3:
				return (
					<ObservationsStep
						form={form}
						handleUpdateForm={handleUpdateForm}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<div style={{ padding: '25px' }}>
			<Stack
				direction='column'
				alignItems='flex-start'
				justifyContent='space-between'
				style={{
					border: '1px solid white',
					padding: '20px 20px 0 0',
					margin: '0 0 10px 0',
					width: '100%',
				}}
			>
				<Typography variant='h4' gutterBottom>
					Editar Prospección
				</Typography>
				<Typography variant='body1' gutterBottom>
					Complete el formulario para actualizar la prospección
					comercial. Asegúrese de llenar todos los campos obligatorios
					para facilitar el seguimiento de la misma.
				</Typography>

				<Box mt={2} mb={2}>
					<Alert severity='info'>
						<b>
							<u>
								Proporcione la información más detallada posible
								sobre el cliente y la situación, esto ayudará a
								tomar mejores decisiones en el proceso de
								seguimiento.
							</u>
						</b>
					</Alert>
				</Box>
			</Stack>

			<ProspectsStepper activeStep={activeStep} steps={steps} />

			{renderStepContent(activeStep)}

			<ProspectsActions
				activeStep={activeStep}
				steps={steps}
				handleBack={handleBack}
				handleNext={handleNext}
				handleShowSummary={handleShowSummary}
				handleCancel={handleCancel}
				handleSubmit={handleSubmit}
				isLoading={isLoading}
			/>

			<SummaryModal
				open={showSummary}
				handleClose={handleCloseSummary}
				form={form}
			/>
		</div>
	);
};
